/**
 * Pure, stateless helpers for fuzzy name matching used by both the
 * server-mirrored `findClientsByIdentity()` submit-time check and the
 * live `useDuplicateMatch()` hook. Keeping them here avoids a circular
 * dependency between `clientService` and `hooks/useDuplicateMatch`.
 */

export interface ClientIdentityProjection {
    id: string;
    firstName: string;
    lastName: string;
    dobYear?: string;
}

export type IdentityMatchCategory = 'name-and-year' | 'similar-name' | 'name-only';

export interface IdentityMatchResult {
    matchCategory?: IdentityMatchCategory;
    id: string;
    firstName: string;
    lastName: string;
    dobYear?: string;
    matchScore: number;
}

export function levenshteinDistance(a: string, b: string): number {
    const m = a.length;
    const n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;
    const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (a[i - 1] === b[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1];
            } else {
                dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
            }
        }
    }
    return dp[m][n];
}

export function nameSimilarity(input: string, candidate: string): number {
    const a = input.trim().toLowerCase();
    const b = candidate.trim().toLowerCase();
    if (!a || !b) return 0;
    if (a === b) return 100;
    const maxLen = Math.max(a.length, b.length);
    return Math.round(((maxLen - levenshteinDistance(a, b)) / maxLen) * 100);
}

/**
 * Strict live-match rule used for typeahead chips:
 *   - Exact first + last AND matching DOB year, OR
 *   - Average fuzzy similarity ≥ 95%.
 * DOB year is consulted only when provided; a missing `dobYear` on a
 * candidate never blocks a fuzzy-only match.
 */
export function findBestIdentityMatch(
    index: ClientIdentityProjection[],
    firstName: string,
    lastName: string,
    dobYear?: string,
): IdentityMatchResult | null {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst || !trimmedLast) return null;

    let best: IdentityMatchResult | null = null;

    for (const candidate of index) {
        if (!candidate.firstName || !candidate.lastName) continue;

        const firstSim = nameSimilarity(trimmedFirst, candidate.firstName);
        const lastSim = nameSimilarity(trimmedLast, candidate.lastName);
        const exactName = firstSim === 100 && lastSim === 100;
        const avgSim = (firstSim + lastSim) / 2;

        const exactWithDob = exactName && !!dobYear && candidate.dobYear === dobYear;
        const strongFuzzy = avgSim >= 95;

        if (!exactWithDob && !strongFuzzy) continue;

        if (dobYear && candidate.dobYear && candidate.dobYear !== dobYear) continue;

        const score = exactWithDob ? 100 : Math.round(avgSim);
        const bestHasYearEvidence = !!dobYear && best?.dobYear === dobYear;
        const candidateHasYearEvidence = !!dobYear && candidate.dobYear === dobYear;
        if (!best || (candidateHasYearEvidence && !bestHasYearEvidence) ||
            (candidateHasYearEvidence === bestHasYearEvidence &&
                (score > best.matchScore || (score === best.matchScore && candidate.id.localeCompare(best.id) < 0)))) {
            best = {
                id: candidate.id,
                firstName: candidate.firstName,
                lastName: candidate.lastName,
                dobYear: candidate.dobYear,
                matchScore: score,
            };
        }
    }

    return best;
}


/** Exact-name advice only; this result must never authorize an association. */
export function findNameOnlyAdvice(
    index: ClientIdentityProjection[], firstName: string, lastName: string, dobYear?: string,
): IdentityMatchResult[] {
    const candidates = index.filter(candidate =>
        nameSimilarity(firstName, candidate.firstName) === 100 &&
        nameSimilarity(lastName, candidate.lastName) === 100 &&
        (!dobYear || !candidate.dobYear || candidate.dobYear !== dobYear),
    ).sort((a, b) => a.id.localeCompare(b.id));
    return candidates.map(candidate => ({ ...candidate, matchScore: 100, matchCategory: 'name-only' }));
}
